import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetMeApi } from 'eds-common/api/v2';
import { Button } from '@scholastic/mockingjay';
import CaretDownIcon from '@data/assets/caret-down.svg';
import CaretUpIcon from '@data/assets/caret-up.svg';
import { isMagazineEdApp, type ProductCode } from '@data/config/reports';
import { getSchoolYearDateRange } from '@data/utils/get-school-year';
import { useGetClassesByEdAppApi } from '@data/hooks/api/use-get-classes-by-ed-app-api/use-get-classes-by-ed-app-api';
import type { DateRange } from './dropdowns/DateRangeDropdown/DateRangeDropdown';
import {
  DateRangeDropdown,
  SCHOOL_YEAR_VALUE,
} from './dropdowns/DateRangeDropdown/DateRangeDropdown';
import { ClassDropdown } from './dropdowns/ClassDropdown/ClassDropdown';
import { StudentDropdown } from './dropdowns/StudentDropdown/StudentDropdown';

export enum Drilldown {
  AllClasses = 'all-classes',
  Class = 'class',
  Student = 'student',
  EngagementFeed = 'engagement-feed',
  /**
   * Admin only — every organization the admin can see, which is `overview`
   * itself. Names a level rather than a URL segment, as `AllClasses` does.
   */
  AllOrganizations = 'all-organizations',
  /** Admin only — the URL segment under a report, as in `overview/org`. */
  Organization = 'org',
  /** Admin only — one educator within one organization. */
  Teacher = 'teacher',
}

const CLASS_ID_KEY = 'classId';
const STUDENT_ID_KEY = 'studentId';
const FROM_KEY = 'from';
const TO_KEY = 'to';
const DATE_PARAM_FORMAT = 'yyyy-MM-dd';

interface ReportFilterBarProps {
  drilldown: Drilldown;
  classIds: string[];
  studentIds: string[];
  edApp: ProductCode;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
}

export function ReportFilterBar({
  drilldown,
  classIds,
  studentIds,
  edApp,
  dateRange,
  onDateRangeChange,
}: ReportFilterBarProps) {
  const { data: user } = useGetMeApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const firstClassId = classIds[0] ?? '';
  // A range carried on the URL means the dropdown should open on "Custom Range".
  const hasUrlDateRange =
    searchParams.has(FROM_KEY) && searchParams.has(TO_KEY);
  const isMagsEdApp = isMagazineEdApp(edApp);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [dateResetKey, setDateResetKey] = useState(0);

  // Auto-close the mobile filter panel when the viewport grows past the
  // tablet breakpoint (640px). Without this, mobileOpen can stay true after
  // a resize, causing stale state even though CSS hides the panel.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)'); // matches 'tablet' breakpoint
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setMobileOpen(false);
    };
    mq.addEventListener('change', handler);
    return () => {
      mq.removeEventListener('change', handler);
    };
  }, []);

  const updateParams = useCallback(
    (updates: Record<string, string | string[] | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            next.delete(key);
            if (value !== null) {
              const values = Array.isArray(value) ? value : [value];
              values.filter(Boolean).forEach((v) => next.append(key, v));
            }
          });
          return next;
        },
        { replace: true, preventScrollReset: true }
      );
    },
    [setSearchParams]
  );

  // Mirror the selected date range into the URL (`from`/`to`), the same way
  // classId/studentId are persisted. Selecting the school-year default clears
  // the params so the "default" state carries no date in the URL.
  const handleDateRangeChange = useCallback(
    (range: DateRange, optionValue: string) => {
      onDateRangeChange(range);
      if (optionValue === SCHOOL_YEAR_VALUE) {
        updateParams({ [FROM_KEY]: null, [TO_KEY]: null });
      } else {
        updateParams({
          [FROM_KEY]: format(range.from, DATE_PARAM_FORMAT),
          [TO_KEY]: format(range.to, DATE_PARAM_FORMAT),
        });
      }
    },
    [onDateRangeChange, updateParams]
  );

  const handleReset = useCallback(() => {
    onDateRangeChange(getSchoolYearDateRange());
    setDateResetKey((k) => k + 1);

    const updates: Record<string, string | string[] | null> = {
      [FROM_KEY]: null,
      [TO_KEY]: null,
    };
    if (drilldown === Drilldown.AllClasses) {
      updates[CLASS_ID_KEY] = null;
    } else if (drilldown === Drilldown.Class) {
      // Keep classId, clear studentId only
      updates[STUDENT_ID_KEY] = null;
    } else if (drilldown === Drilldown.EngagementFeed) {
      updates[CLASS_ID_KEY] = classIds.length === 1 ? firstClassId : null;
      updates[STUDENT_ID_KEY] = null;
    }
    // 'student' — only date resets (plus date params cleared above)
    updateParams(updates);
  }, [
    classIds.length,
    drilldown,
    firstClassId,
    onDateRangeChange,
    updateParams,
  ]);

  const handleClassesChange = useCallback(
    (ids: string[]) =>
      updateParams({ [CLASS_ID_KEY]: ids, [STUDENT_ID_KEY]: null }),
    [updateParams]
  );

  const handleEngagementClassesChange = useCallback(
    (ids: string[]) => {
      const updates: Record<string, string | string[] | null> = {
        [CLASS_ID_KEY]: ids,
      };
      // Clear student filter when not exactly one class
      if (ids.length !== 1) {
        updates[STUDENT_ID_KEY] = null;
      }
      updateParams(updates);
    },
    [updateParams]
  );

  const handleClassChange = useCallback(
    (id: string) =>
      updateParams({ [CLASS_ID_KEY]: id, [STUDENT_ID_KEY]: null }),
    [updateParams]
  );

  const handleStudentsChange = useCallback(
    (ids: string[]) => updateParams({ [STUDENT_ID_KEY]: ids }),
    [updateParams]
  );

  const handleStudentChange = useCallback(
    (id: string) => updateParams({ [STUDENT_ID_KEY]: id }),
    [updateParams]
  );

  // The same classes ClassDropdown lists, from the same cached query — scoped to
  // this edApp and narrowed to classes with students. It has to be this list,
  // not the teacher's org-wide one: a teacher with three classes but only one
  // entitled to this product sees a single option in the dropdown, and the
  // filters below have to agree with what is on screen.
  const { data: edAppClasses } = useGetClassesByEdAppApi(
    {
      orgId: user?.currentOrg?.orgId ?? '',
      userId: user?.userId ?? '',
      edApp,
    },
    { enabled: !!user && drilldown === Drilldown.EngagementFeed, retry: 0 }
  );

  const selectableClasses = useMemo(() => edAppClasses ?? [], [edAppClasses]);

  // Engagement feed: which class the student filter belongs to.
  //
  // Either the teacher selected exactly one class, or the dropdown only offers
  // one — in which case that class is what is being viewed whether or not it is
  // explicitly selected, since "All classes" over a single class is that class.
  const engagementSingleClassId = useMemo(() => {
    if (drilldown !== Drilldown.EngagementFeed) return null;
    if (classIds.length === 1) return classIds[0] ?? null;
    if (selectableClasses.length === 1) {
      return selectableClasses[0]?.classId ?? null;
    }
    return null;
  }, [drilldown, classIds, selectableClasses]);

  // Engagement feed: when no class is selected in the URL but only one is
  // available, write it to the URL so downstream consumers always have a
  // concrete classId to work with.
  useEffect(() => {
    if (
      drilldown === Drilldown.EngagementFeed &&
      classIds.length === 0 &&
      selectableClasses.length === 1
    ) {
      const singleClassId = selectableClasses[0]?.classId;
      if (singleClassId) {
        updateParams({ [CLASS_ID_KEY]: [singleClassId] });
      }
    }
  }, [drilldown, classIds.length, selectableClasses, updateParams]);

  const renderDrilldownFilters = () => {
    if (!user) return null;

    switch (drilldown) {
      case Drilldown.AllClasses:
        return (
          <div className="flex-1">
            <ClassDropdown
              mode="multiselect"
              selectedClassIds={classIds}
              orgId={user.currentOrg.orgId}
              userId={user.userId}
              edApp={edApp}
              onChange={handleClassesChange}
            />
          </div>
        );

      case Drilldown.Class:
        return (
          <>
            <div className="flex-1">
              <ClassDropdown
                mode="single"
                orgId={user.currentOrg.orgId}
                userId={user.userId}
                edApp={edApp}
                selectedClassId={firstClassId}
                onChange={handleClassChange}
              />
            </div>
            {firstClassId && (
              <div className="flex-1">
                <StudentDropdown
                  mode="multiselect"
                  edApp={edApp}
                  classId={firstClassId}
                  selectedStudentIds={studentIds}
                  onChange={handleStudentsChange}
                />
              </div>
            )}
          </>
        );

      case Drilldown.Student:
        return (
          <>
            <div className="flex-1">
              <ClassDropdown
                mode="locked"
                lockedClassId={firstClassId}
                orgId={user.currentOrg.orgId}
                userId={user.userId}
                edApp={edApp}
              />
            </div>
            <div className="flex-1">
              <StudentDropdown
                mode="single"
                edApp={edApp}
                classId={firstClassId}
                selectedStudentId={studentIds[0] ?? ''}
                onChange={handleStudentChange}
              />
            </div>
          </>
        );

      case Drilldown.EngagementFeed:
        return (
          <>
            <div className="flex-1">
              <ClassDropdown
                mode="multiselect"
                selectedClassIds={classIds}
                orgId={user.currentOrg.orgId}
                userId={user.userId}
                edApp={edApp}
                onChange={handleEngagementClassesChange}
              />
            </div>
            {engagementSingleClassId && (
              <div className="flex-1">
                <StudentDropdown
                  mode="multiselect"
                  edApp={edApp}
                  classId={engagementSingleClassId}
                  selectedStudentIds={studentIds}
                  onChange={handleStudentsChange}
                />
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex w-full flex-col gap-1 @container">
      {/* Mobile toggle — hidden on tablet+ */}
      <div className="mb-1 tablet:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          className="flex w-full items-center gap-3 rounded-lg bg-white px-2 py-3"
        >
          {mobileOpen ? (
            <CaretUpIcon aria-hidden className="size-4 shrink-0" />
          ) : (
            <CaretDownIcon aria-hidden className="size-4 shrink-0" />
          )}
          <span className="text-base font-semibold text-neutral-1100">
            {mobileOpen ? 'Hide filter bar' : 'Show filter bar'}
          </span>
        </button>
      </div>

      {/* Collapsible content — hidden on mobile when closed, always visible on tablet+ */}
      <div className={`${mobileOpen ? 'block' : 'hidden'} tablet:block`}>
        <div className="flex w-full flex-col items-stretch gap-3 @[555px]:flex-row @[555px]:items-end">
          <div
            className={`flex ${
              isMagsEdApp ? '' : 'flex-1'
            } flex-col gap-3 @[555px]:flex-row`}
          >
            <div className="flex-1">
              <DateRangeDropdown
                key={dateResetKey}
                onChange={handleDateRangeChange}
                initialCustomRange={hasUrlDateRange ? dateRange : null}
                useMaxWidth={isMagsEdApp}
              />
            </div>
            {/* Magazine ed apps: only DateRange dropdown is shown */}
            {!isMagsEdApp && renderDrilldownFilters()}
          </div>
          <Button
            variant="secondary"
            className="w-auto shrink-0 self-end border-none"
            onClick={handleReset}
          >
            Reset filters
          </Button>
        </div>
        <p className="mt-3 text-sm text-neutral-1300">
          Student data for {format(dateRange.from, 'MM/dd/yyyy')} -{' '}
          {format(dateRange.to, 'MM/dd/yyyy')}
        </p>
        <div className="my-8 h-[1px] bg-neutral-400" />
      </div>
    </div>
  );
}
